import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import "./PaymentsPage.scss";
import "./../../Style/AddPurchaseTotal.scss";
import { deleteAlert, errorAlert } from "../../Components/Alerts";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { AiFillAppstore } from "react-icons/ai";
import "./../../Style/TableStyle.scss";
import "./../../Style/__button.scss";
import { PaymentGridHeader } from "./PaymentGridHeader";
import ReusableServerDataGrid from "../../Components/ReusableServerDataGrid";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import { AddElementsButton } from "../../Components/AddElementsButton";
import CrudPageHeader from "../../Components/CrudPageHeader";
import { formatDate } from "../../Components/formatDate";
import PaymentsViewModal from "./PaymentsViewModal";

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatPaymentNumber = (item, fallbackYear) => {
  const rawInvoiceNr = item?.InvoiceNr != null ? String(item.InvoiceNr).trim() : "";
  const paymentYear =
    item?.FiscalYear ||
    item?.Year ||
    (item?.Date ? new Date(item.Date).getFullYear() : null) ||
    fallbackYear;

  if (!rawInvoiceNr) {
    return "-";
  }

  if (rawInvoiceNr.toUpperCase().startsWith("PAY-")) {
    return rawInvoiceNr;
  }

  return paymentYear ? `PAY-${paymentYear}-${rawInvoiceNr}` : `PAY-${rawInvoiceNr}`;
};

export default function PaymentsPage() {
  const history = useHistory();
  const { fiscalYear } = useContext(FiscalYearContext);
  const headers = authHeader();

  const [data, setData] = useState([]);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 50,
  });
  const [sortModel, setSortModel] = React.useState([]);
  const [filterModel, setFilterModel] = React.useState({ items: [] });
  const [quickFilter, setQuickFilter] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [totals, setTotals] = useState({
    Total: 0,
  });

  const fetchTotals = async (fiscal, activeFilterItems = [], activeQuickFilter = []) => {
    try {
      const response = await axios.get(
        ApiLink.API_URL + "Payments/getTotals",
        {
          headers,
          params: {
            fiscalYear: fiscal,
            filter: JSON.stringify(activeFilterItems || []),
            quickFilter: (activeQuickFilter || []).join(" "),
          },
        },
      );
      setTotals(response.data);
    } catch (error) {
      errorAlert("Gabim në marrjen e totaleve të pagesave.");
    }
  };

  const fetchData = async () => {
    setLoading(true);

    try {
      const response = await axios.get(
        `${ApiLink.API_URL}Payments/getAllPaymentsData`,
        {
          headers,
          params: {
            fiscalYear,
            page: paginationModel.page,
            pageSize: paginationModel.pageSize,
            sort: JSON.stringify(sortModel),
            filter: JSON.stringify(filterModel.items),
            quickFilter: quickFilter.join(" "),
          },
        },
      );

      setData(response.data.items || []);
      setRowCount(response.data.total || 0);
    } catch (error) {
      errorAlert("Gabim në marrjen e listës së pagesave.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (deleteItemId) => {
    try {
      await axios.delete(ApiLink.API_URL + "Payments" + `/${deleteItemId}`, {
        headers,
      });
      deleteAlert("Pagesa është fshirë me sukses");
      fetchData();
      fetchTotals(fiscalYear, filterModel.items, quickFilter);
    } catch (error) {
      deleteAlert("Pagesa nuk është fshirë me sukses");
    }
  };

  const handleEdit = (item) => {
    history.push(`/money/editpayments/${item.TransactionID}`);
  };

  const handleView = (item) => {
    setViewData(item);
    setShowViewModal(true);
  };

  const fetchExportRows = async () => {
    const totalRowsToExport = Math.max(Number(rowCount) || 0, 1);

    const response = await axios.get(`${ApiLink.API_URL}Payments/getAllPaymentsData`, {
      headers,
      params: {
        fiscalYear,
        page: 0,
        pageSize: totalRowsToExport,
        sort: JSON.stringify(sortModel),
        filter: JSON.stringify(filterModel.items),
        quickFilter: quickFilter.join(" "),
      },
    });

    const items = response?.data?.items || [];
    return items.map((item, index) => ({
      ...item,
      rowNumber: index + 1,
      InvoiceNr: formatPaymentNumber(item, fiscalYear),
      Date: formatDate(item.Date),
      AccountName: item.Account?.AccountName ?? item.AccountName,
      LedgerName: item.Account?.AccountName_PaymentOptions ?? "-",
      Total: item.Total,
      rowInfo: item,
    }));
  };

  const rows = data.map((item, index) => ({
    id: item.TransactionID ?? `payment-row-${index}`,
    rowNumber: paginationModel.page * paginationModel.pageSize + index + 1,
    InvoiceNr: formatPaymentNumber(item, fiscalYear),
    Date: formatDate(item.Date),
    AccountName: item.Account?.AccountName ?? item.AccountName,
    LedgerName: item.Account?.AccountName_PaymentOptions ?? "-",
    Total: item.Total,
    deleteItemId: item.TransactionID,
    rowInfo: item,
  }));

  useEffect(() => {
    fetchTotals(fiscalYear, filterModel.items, quickFilter);
  }, [fiscalYear, filterModel, quickFilter]);

  React.useEffect(() => {
    fetchData();
  }, [paginationModel, sortModel, filterModel, quickFilter, fiscalYear]);

  return (
    <div className="Crud__Container PAYM__page">
      <div className="PAYM__content">
        <CrudPageHeader
          icon={AiFillAppstore}
          title="Lista e Pagesave"
          description="Forma e Pagesave: Menaxhoni, shtoni, editoni dhe fshini Pagesa."
          tutorialUrl="https://www.youtube.com/@Accountree"
          tutorialLabel="Shiko Tutorialin"
          className="PAYM__header"
          action={
            <AddElementsButton
              text="Krijo Pagesë"
              icon="bi bi-plus-circle"
              className="button-68"
              onClick={() => history.push("/money/addpayments")}
            />
          }
        />

        <ReusableServerDataGrid
          columns={PaymentGridHeader}
          rows={rows}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          showViewAction={true}
          actionsMinWidth={90}
          actionsFlex={0.75}
          height={620}
          rowCount={rowCount}
          loading={loading}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          sortModel={sortModel}
          setSortModel={setSortModel}
          filterModel={filterModel}
          setFilterModel={setFilterModel}
          quickFilter={quickFilter}
          setQuickFilter={setQuickFilter}
          getExportRows={fetchExportRows}
          summaryFooter={{
            labelField: "AccountName",
            label: "Totali",
            values: {
              Total: formatCurrency(totals.Total),
            },
          }}
        />

        <PaymentsViewModal
          show={showViewModal}
          onHide={() => setShowViewModal(false)}
          data={viewData}
        />
      </div>
    </div>
  );
}
